import React from "react";
import { Link } from "react-router-dom";
import { Droplets, Sparkles, ScanLine, Waves, Layers, FlaskConical, ClipboardCheck, PackageOpen, ArrowRight, ArrowUpRight } from "lucide-react";
import { Seo, breadcrumbSchema } from "@/components/Seo";
import { SERVICE_HUB } from "@/data/servicesHub";

export const SERVICE_ICONS = {
  droplets: Droplets, sparkles: Sparkles, scan: ScanLine, waves: Waves,
  layers: Layers, flask: FlaskConical, clipboard: ClipboardCheck, boxes: PackageOpen,
};

export default function ServicesHub() {
  return (
    <div className="bg-ink min-h-screen pb-24">
      <Seo
        title="Trading Card Services — Cleaning, Rehab & Grading Prep | CardScience HQ"
        description="Professional trading card services: surface cleaning, polishing, scratch, dent and crease reduction, full rehabilitation, pre-grading preparation and safe slab removal. Each service explained in detail. No guaranteed grades."
        path="/services"
        jsonLd={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Services", path: "/services" }])}
      />

      <section className="relative overflow-hidden py-20 sm:py-24">
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(60% 55% at 50% 0%, rgba(59,123,255,0.16), transparent 65%)" }} />
        <div className="relative mx-auto max-w-[1300px] px-5">
          <div className="text-center max-w-3xl mx-auto">
            <p className="eyebrow mb-4">CardScience HQ · The Lab Menu</p>
            <h1 className="h-display text-4xl sm:text-5xl lg:text-6xl text-chrome leading-[1.02]">
              PROFESSIONAL<span className="block text-holo mt-1">CARD SERVICES.</span>
            </h1>
            <p className="text-[color:var(--chrome-2)] mt-6 leading-relaxed">
              We professionally clean, improve, rehabilitate and safely remove trading cards from slabs — without
              unrealistic restoration promises. Explore each service in detail below.
            </p>
          </div>

          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {SERVICE_HUB.map((s) => {
              const Icon = SERVICE_ICONS[s.iconKey] || Droplets;
              return (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  data-testid={`hub-card-${s.slug}`}
                  className="group relative rounded-2xl border border-[color:var(--line)] bg-white/[0.02] p-6 flex flex-col transition-all duration-300 hover:-translate-y-1.5 hover:border-[color:var(--cyan)]/60 noise"
                >
                  <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
                    style={{ background: "radial-gradient(80% 60% at 50% 0%, rgba(36,224,255,0.10), transparent 70%)" }} />
                  <div className="relative h-11 w-11 rounded-xl bg-white/[0.04] border border-[color:var(--line)] flex items-center justify-center text-[color:var(--cyan)] group-hover:text-white transition-colors">
                    <Icon size={20} />
                  </div>
                  <h2 className="relative font-display font-black text-lg text-white mt-5 leading-tight">{s.name}</h2>
                  <p className="relative text-sm text-[color:var(--chrome-2)] mt-2 leading-relaxed flex-1">{s.short}</p>
                  <span className="relative inline-flex items-center gap-1.5 text-[11px] font-tech tracking-widest text-[color:var(--cyan)] mt-5 group-hover:gap-2.5 transition-all">
                    EXPLORE THIS SERVICE <ArrowUpRight size={14} />
                  </span>
                </Link>
              );
            })}
          </div>

          <div className="mt-14 text-center">
            <Link to="/submit" data-testid="hub-cta" className="btn-holo inline-flex items-center gap-2 rounded-xl px-8 py-4 text-sm">
              START MY CARD EVALUATION <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
